/**
 * useUploads Hook
 *
 * Manages upload state for structures/UPRNs, including:
 * - Fetching existing uploads from API
 * - Tracking pending uploads
 * - Tag management (add/remove)
 * - Delete functionality
 * - Syncing with legacy JavaScript upload system
 */
import { useState, useCallback, useEffect, useRef } from 'react';
// Re-export shared utilities for backwards compatibility
export { processImageUrl, isImageFile } from '../../../utils/uploads';
import { processImageUrl } from '../../../utils/uploads';
export function useUploads({ objectType, objectId, autoFetch = true }) {
    const [uploads, setUploads] = useState([]);
    const [pendingUploads, setPendingUploads] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    // Track if component is mounted to avoid state updates after unmount
    const isMounted = useRef(true);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    /**
     * Fetch uploads from the API
     */
    const fetchUploads = useCallback(async () => {
        if (!objectId)
            return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await fetch(`/upload/assets/${objectType}/${objectId}`);
            if (!response.ok) {
                throw new Error(`Failed to fetch uploads: ${response.statusText}`);
            }
            const data = await response.json();
            if (isMounted.current) {
                // API returns array directly
                const uploadList = Array.isArray(data) ? data : [];
                // Process URLs for thumbnails
                const processedUploads = uploadList.map((upload) => ({
                    ...upload,
                    processedUrl: upload.url ? processImageUrl(upload.url) : undefined,
                }));
                setUploads(processedUploads);
            }
        }
        catch (err) {
            console.error('[useUploads] Error fetching uploads:', err);
            if (isMounted.current) {
                setError(err instanceof Error ? err.message : 'Failed to fetch uploads');
            }
        }
        finally {
            if (isMounted.current) {
                setIsLoading(false);
            }
        }
    }, [objectType, objectId]);
    /**
     * Add a pending upload (before it's uploaded to server)
     */
    const addPendingUpload = useCallback((upload) => {
        setPendingUploads(prev => {
            // Check if already exists
            if (prev.some(p => p.filename === upload.filename)) {
                return prev;
            }
            return [...prev, { ...upload, seq: prev.length }];
        });
    }, []);
    /**
     * Update a pending upload (e.g., with preview data)
     */
    const updatePendingUpload = useCallback((filename, updates) => {
        setPendingUploads(prev => prev.map(p => (p.filename === filename ? { ...p, ...updates } : p)));
    }, []);
    /**
     * Complete a pending upload - move it from pending to completed
     */
    const completePendingUpload = useCallback((filename, uploadData) => {
        setPendingUploads(prev => prev.filter(p => p.filename !== filename));
        setUploads(prev => {
            // Check if already exists (by id)
            if (prev.some(u => u.id === uploadData.id)) {
                return prev;
            }
            return [
                {
                    ...uploadData,
                    processedUrl: uploadData.url ? processImageUrl(uploadData.url) : undefined,
                },
                ...prev,
            ];
        });
    }, []);
    /**
     * Remove a pending upload (e.g., on error)
     */
    const removePendingUpload = useCallback((filename) => {
        setPendingUploads(prev => prev.filter(p => p.filename !== filename));
    }, []);
    /**
     * Delete an upload from the server
     */
    const deleteUpload = useCallback(async (uploadId) => {
        try {
            const response = await fetch(`/upload/assets/${uploadId}`, {
                method: 'DELETE',
            });
            const result = await response.json();
            if (result.status === 'OK') {
                if (isMounted.current) {
                    setUploads(prev => prev.filter(u => u.id !== uploadId));
                }
                return true;
            }
            else {
                console.error('[useUploads] Delete failed:', result.msg);
                return false;
            }
        }
        catch (err) {
            console.error('[useUploads] Error deleting upload:', err);
            return false;
        }
    }, []);
    /**
     * Add a tag to an upload
     */
    const addTag = useCallback(async (uploadId, tag) => {
        try {
            const response = await fetch(`/upload/assets/${uploadId}/tag`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ tag }),
            });
            const result = await response.json();
            if (result.status === 'OK' && result.data) {
                if (isMounted.current) {
                    setUploads(prev => prev.map(u => {
                        if (u.id === uploadId) {
                            return {
                                ...u,
                                UploadTags: [...(u.UploadTags || []), result.data],
                            };
                        }
                        return u;
                    }));
                }
                return result.data;
            }
            return null;
        }
        catch (err) {
            console.error('[useUploads] Error adding tag:', err);
            return null;
        }
    }, []);
    /**
     * Remove a tag from an upload
     */
    const removeTag = useCallback(async (uploadId, tag) => {
        try {
            const response = await fetch(`/upload/assets/${uploadId}/tag/${encodeURIComponent(tag)}`, {
                method: 'DELETE',
            });
            const result = await response.json();
            if (result.status === 'OK') {
                if (isMounted.current) {
                    setUploads(prev => prev.map(u => {
                        if (u.id === uploadId) {
                            return {
                                ...u,
                                UploadTags: (u.UploadTags || []).filter(t => t.tag !== tag),
                            };
                        }
                        return u;
                    }));
                }
                return true;
            }
            return false;
        }
        catch (err) {
            console.error('[useUploads] Error removing tag:', err);
            return false;
        }
    }, []);
    /**
     * Refresh uploads from server
     */
    const refresh = useCallback(async () => {
        await fetchUploads();
    }, [fetchUploads]);
    // Auto-fetch on mount if enabled
    useEffect(() => {
        if (autoFetch && objectId) {
            fetchUploads();
        }
    }, [autoFetch, objectId, fetchUploads]);
    return {
        uploads,
        pendingUploads,
        isLoading,
        error,
        fetchUploads,
        addPendingUpload,
        updatePendingUpload,
        completePendingUpload,
        removePendingUpload,
        deleteUpload,
        addTag,
        removeTag,
        refresh,
    };
}
export default useUploads;
